/**
 * StoreProvider Component
 * 
 * Library-agnostic store provider.
 * Provides store context to all child components.
 * Uses adapter-specific providers internally.
 */

import React from 'react';
import { StoreContext, type StoreContextValue } from '../context/StoreContext';
import type { IStoreProviderProps, IStore } from '../types';
import { ReduxStoreAdapter } from '../adapters/reduxAdapter';
import { ReduxProviderAdapter } from '../adapters/reduxProviderAdapter';

/**
 * StoreProvider component
 * 
 * Provides store context to all child components and handles persistence.
 * Automatically detects the store adapter type and uses the appropriate provider.
 * 
 * @example
 * ```typescript
 * <StoreProvider store={store}>
 *   <App />
 * </StoreProvider>
 * ```
 */
export function StoreProvider<State = unknown>({
  store,
  children,
  loading,
}: IStoreProviderProps<State>): JSX.Element {
  // Create context value - cast to unknown to match context type
  const contextValue = React.useMemo(
    () => ({
      store: store as IStore<unknown>,
    }),
    [store],
  ) as StoreContextValue<unknown>;

  // Check if store is a Redux adapter
  if (store && 'getReduxStore' in store) {
    const reduxAdapter = store as ReduxStoreAdapter<State>;
    const storeWithPersistence = store as typeof store & { persistor?: any };
    
    // Get Redux persistor if available
    const persistor = storeWithPersistence.persistor?.getReduxPersistor?.();

    return (
      <StoreContext.Provider value={contextValue}>
        <ReduxProviderAdapter
          store={reduxAdapter}
          persistor={persistor}
          loading={loading}
        >
          {children}
        </ReduxProviderAdapter>
      </StoreContext.Provider>
    );
  }

  // Fallback: just use context (for non-Redux implementations)
  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
}
